// kast-dev family file: diagnostics; master: waypoint_wars/ui/common/utils/diagnostics.ts
// Copied unchanged into every kast-dev app. Change the master, copy it
// everywhere, and run infra/scripts/check-family-files.sh.
//
// The diagnostics kit: a logger that buffers even in production, the API call
// log, the screen history, and the bug report built from all three. No React,
// no DOM, no React Native, so web and mobile share it. One self-contained file
// so a copy can live at any path without fixing up imports. The web report
// form (BugReportForm.tsx) imports it as "@common/utils/diagnostics".

// ---------------------------------------------------------------------------
// Buffers
// ---------------------------------------------------------------------------

type Listener = () => void;

/** A newest-first ring buffer that notifies subscribers when it changes. */
function createBuffer<T extends { id: number }>(max: number) {
  let entries: T[] = [];
  let nextId = 1;
  const listeners = new Set<Listener>();
  const notify = () => listeners.forEach((fn) => fn());
  return {
    add(entry: Omit<T, "id">) {
      entries = [{ ...entry, id: nextId++ } as T, ...entries].slice(0, max);
      notify();
    },
    latest: () => entries[0] as T | undefined,
    // A stable reference between changes, as useSyncExternalStore requires.
    get: (): readonly T[] => entries,
    clear() {
      entries = [];
      notify();
    },
    subscribe(fn: Listener): () => void {
      listeners.add(fn);
      return () => {
        listeners.delete(fn);
      };
    },
  };
}

// ---------------------------------------------------------------------------
// Logger
// ---------------------------------------------------------------------------

/**
 * Passes through to the console only in development, but always records, so
 * a report from a production build still carries what happened. Metro sets
 * __DEV__ and Node (tests) has NODE_ENV. Vite defines neither in the
 * browser, so there a page served from localhost counts as development.
 */
const isDev: boolean = (() => {
  const g = globalThis as { __DEV__?: boolean; location?: { hostname?: string } };
  if (typeof g.__DEV__ !== "undefined") return Boolean(g.__DEV__);
  if (typeof process !== "undefined" && process.env?.NODE_ENV) return process.env.NODE_ENV !== "production";
  return /^(localhost|127\.0\.0\.1|\[::1\])$/.test(g.location?.hostname ?? "");
})();

export type LogLevel = "log" | "warn" | "error";

export interface LogEntry {
  id: number;
  timestamp: number;
  level: LogLevel;
  message: string;
}

const MAX_MESSAGE_CHARS = 2000;
const consoleBuffer = createBuffer<LogEntry>(200);

function formatArgs(args: unknown[]): string {
  const text = args
    .map((arg) => {
      if (typeof arg === "string") return arg;
      if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
      try {
        return JSON.stringify(arg);
      } catch {
        return String(arg); // circular refs, BigInt
      }
    })
    .join(" ");
  return text.length > MAX_MESSAGE_CHARS ? `${text.slice(0, MAX_MESSAGE_CHARS)}…` : text;
}

function logAt(level: LogLevel) {
  return (...args: unknown[]) => {
    consoleBuffer.add({ timestamp: Date.now(), level, message: formatArgs(args) });
    if (isDev) console[level](...args);
  };
}

/** Drop-in for console.log/warn/error. */
export const logger = { log: logAt("log"), warn: logAt("warn"), error: logAt("error") };

/** Newest first. */
export const getLogBuffer = consoleBuffer.get;
export const clearLogBuffer = consoleBuffer.clear;
export const subscribeLogBuffer = consoleBuffer.subscribe;

// ---------------------------------------------------------------------------
// API call log
// ---------------------------------------------------------------------------

export interface ApiLogEntry {
  id: number;
  timestamp: number;
  method: string;
  /** A path or URL, never a header or a body. Redacted before it is sent. */
  path: string;
  /** null for a network error or timeout. */
  status: number | null;
  durationMs: number;
  error: string | null;
}

const apiBuffer = createBuffer<ApiLogEntry>(200);

/** Record a finished API call. Call it from the app's API client, or use installFetchRecorder. */
export const recordApiCall = (entry: Omit<ApiLogEntry, "id">): void => apiBuffer.add(entry);
/** Newest first. */
export const getApiLog = apiBuffer.get;
export const clearApiLog = apiBuffer.clear;
export const subscribeApiLog = apiBuffer.subscribe;

// ---------------------------------------------------------------------------
// Navigation log
// ---------------------------------------------------------------------------

export interface NavLogEntry {
  id: number;
  timestamp: number;
  screen: string;
}

const navBuffer = createBuffer<NavLogEntry>(100);

/** Record a screen visit. A repeat of the current screen is ignored. */
export function recordNavigation(screen: string): void {
  if (navBuffer.latest()?.screen === screen) return;
  navBuffer.add({ timestamp: Date.now(), screen });
}
/** Newest first. */
export const getNavLog = navBuffer.get;
export const clearNavLog = navBuffer.clear;
export const subscribeNavLog = navBuffer.subscribe;

/** Empty all three logs. */
export function clearDiagnostics(): void {
  clearApiLog();
  clearLogBuffer();
  clearNavLog();
}

// ---------------------------------------------------------------------------
// Redaction
// ---------------------------------------------------------------------------

export const REDACTED = "[redacted]";

const SECRET_KEY = /^(authorization|cookie|token|id_?token|access_?token|refresh_?token|password|secret|api_?key|credential)s?$/i;

const SECRET_PATTERNS: RegExp[] = [
  /\bBearer\s+[\w-]+\.[\w-]+\.[\w-]+/gi, // bearer + JWT
  /\bBearer\s+[A-Za-z0-9._~+/-]{8,}=*/gi, // bearer + opaque
  /\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9._-]+/g, // bare JWT
  /([?&#](?:access_token|id_token|refresh_token|token|code|password|secret|api_?key)=)[^&#\s]+/gi,
  // Presigned S3 and CloudFront URLs: the signature and credentials grant the
  // object to whoever holds the link until it expires.
  /([?&](?:X-Amz-Signature|X-Amz-Credential|X-Amz-Security-Token|Signature|AWSAccessKeyId|Key-Pair-Id|Policy)=)[^&#\s]+/gi,
];

/** Strip tokens and secret query parameters from a string. */
export function redactString(input: string): string {
  let out = input;
  for (const pattern of SECRET_PATTERNS) {
    out = out.replace(pattern, (_match, prefix?: string) => (prefix ? `${prefix}${REDACTED}` : REDACTED));
  }
  return out;
}

/** Recursively strip secrets from any value, by key name and by content. */
export function redactValue<T>(value: T): T {
  if (typeof value === "string") return redactString(value) as T;
  if (Array.isArray(value)) return value.map(redactValue) as T;
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value)) {
      out[key] = SECRET_KEY.test(key) ? REDACTED : redactValue(val);
    }
    return out as T;
  }
  return value;
}

/**
 * A URL fit for a log: no user:password, no fragment (where OAuth implicit
 * flows put tokens), and no secret query values.
 */
export function redactUrl(url: string): string {
  return redactString(url.replace(/^([a-z][a-z0-9+.-]*:\/\/)[^/@\s]*@/i, "$1").replace(/#.*$/, ""));
}

/**
 * UTF-8 byte length without TextEncoder, which neither React Native nor Expo
 * guarantees. A UTF-16 `length` undercounts non-ASCII text by half or more,
 * which would let an oversized report past the cap.
 */
export function utf8ByteLength(str: string): number {
  let bytes = 0;
  for (let i = 0; i < str.length; i++) {
    const code = str.charCodeAt(i);
    if (code < 0x80) bytes += 1;
    else if (code < 0x800) bytes += 2;
    else if (code >= 0xd800 && code <= 0xdbff) {
      bytes += 4; // a surrogate pair is one four-byte code point
      i++;
    } else bytes += 3;
  }
  return bytes;
}

// ---------------------------------------------------------------------------
// The report
// ---------------------------------------------------------------------------

/** DynamoDB caps an item at 400KB; stay well under it after JSON encoding. */
const MAX_BUNDLE_BYTES = 250_000;
/** The admin API rejects a longer description. */
export const MAX_DESCRIPTION_CHARS = 5000;

export type Severity = "low" | "medium" | "high" | "critical";
export const SEVERITIES: readonly Severity[] = ["low", "medium", "high", "critical"];

/** The shape kast-dev-admin's POST /bug-reports accepts and its Bug Reports tab shows. */
export interface BugReportBundle {
  app: string;
  description: string;
  severity: Severity;
  clientTimestamp: number;
  env: string | null;
  user: { id: string | null; email: string | null } | null;
  device: Record<string, unknown> | null;
  apiLog: ApiLogEntry[];
  consoleLog: LogEntry[];
  navLog: NavLogEntry[];
  /** Oldest log entries were dropped to fit the size cap. */
  truncated: boolean;
}

export interface BuildBugReportOptions {
  /** The app's id in the inbox, e.g. "waypoint_wars". */
  app: string;
  description: string;
  severity: Severity;
  env?: string | null;
  user?: { id: string | null; email: string | null } | null;
  device?: Record<string, unknown> | null;
}

/**
 * A redacted, size-capped bundle of the current logs. Everything in it crosses
 * the network, so redaction is not optional: tokens routinely turn up in
 * logged URLs and error messages. Drops the oldest log entries until it fits.
 */
export function buildBugReport(options: BuildBugReportOptions): BugReportBundle {
  const bundle: BugReportBundle = redactValue({
    app: options.app,
    description: options.description.slice(0, MAX_DESCRIPTION_CHARS),
    severity: options.severity,
    clientTimestamp: Date.now(),
    env: options.env ?? null,
    user: options.user ?? null,
    device: options.device ?? null,
    apiLog: getApiLog().map((e) => ({ ...e, path: redactUrl(e.path) })),
    consoleLog: [...getLogBuffer()],
    navLog: [...getNavLog()],
    truncated: false,
  });

  // Newest first, so popping the tail sheds the oldest entries.
  while (utf8ByteLength(JSON.stringify(bundle)) > MAX_BUNDLE_BYTES) {
    const longest = [bundle.apiLog, bundle.consoleLog, bundle.navLog].sort((a, b) => b.length - a.length)[0];
    if (longest.length === 0) break;
    longest.pop();
    bundle.truncated = true;
  }
  return bundle;
}

// ---------------------------------------------------------------------------
// Submission
// ---------------------------------------------------------------------------

/**
 * The central inbox (kast-dev-admin's Bug Reports tab). Fixed rather than
 * following an app's environment toggle: a report about a bug seen against
 * localhost still needs to reach the real inbox. The site's origin must be on
 * the shared API Gateway's CORS list (infra/terraform/terraform.tfvars).
 */
export const BUG_REPORT_URL = "https://api.kast-dev.net/admin-api/bug-reports";
const SUBMIT_TIMEOUT_MS = 15_000;

export interface SubmitOptions {
  /** A kast-dev Cognito token. The inbox refuses anonymous reports. */
  token: string | null | undefined;
  fetchImpl?: typeof fetch;
  timeoutMs?: number;
}

/** POST a bundle to the inbox. Resolves to the report id; rejects with a message fit to show. */
export async function submitBugReport(bundle: BugReportBundle, { token, fetchImpl, timeoutMs = SUBMIT_TIMEOUT_MS }: SubmitOptions): Promise<string> {
  if (!token) throw new Error("You must be signed in to submit a bug report.");

  // An abort rather than a race, so a timeout cancels the request itself.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    // Logged so a failed submission leaves a trace in the next report.
    logger.log("[BugReport] POST /bug-reports");
    const res = await (fetchImpl ?? globalThis.fetch)(BUG_REPORT_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(bundle),
      signal: controller.signal,
    });
    if (!res.ok) {
      let detail = `${res.status}`;
      try {
        detail = (await res.json())?.detail ?? detail;
      } catch {
        // A non-JSON body (a gateway error page); the status is enough.
      }
      logger.error(`[BugReport] submission failed: ${detail}`);
      throw new Error(`Submission failed: ${detail}`);
    }
    const reportId: string = (await res.json())?.report_id ?? "";
    logger.log(`[BugReport] submitted ${reportId}`);
    return reportId;
  } catch (err: unknown) {
    if (controller.signal.aborted) {
      logger.error("[BugReport] submission timed out");
      throw new Error("Submission timed out.", { cause: err });
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

// ---------------------------------------------------------------------------
// Optional: record every fetch
// ---------------------------------------------------------------------------

let inFlight = 0;
const inFlightListeners = new Set<Listener>();

/** Requests the fetch recorder has seen start and not finish, for a busy indicator. */
export const getInFlightCount = (): number => inFlight;
export function subscribeInFlight(fn: Listener): () => void {
  inFlightListeners.add(fn);
  return () => {
    inFlightListeners.delete(fn);
  };
}
function setInFlight(delta: number) {
  inFlight += delta;
  inFlightListeners.forEach((fn) => fn());
}

export interface FetchRecorderOptions {
  /** Where fetch lives. Defaults to globalThis; tests pass their own. */
  target?: { fetch: typeof fetch };
  /** Skip a call: analytics beacons, the dev server's HMR, and so on. */
  ignore?: (url: string) => boolean;
}

/**
 * Wrap the global fetch so every call lands in the API log, for apps whose
 * API client does not call recordApiCall. Use one or the other, not both, or
 * each call is logged twice. Records the method, the redacted URL, the status
 * and the time taken: never a header or a body. Returns an uninstall function;
 * installing twice is a no-op.
 */
export function installFetchRecorder({ target = globalThis, ignore }: FetchRecorderOptions = {}): () => void {
  const original = target.fetch;
  if ((original as { __recorded?: boolean }).__recorded) return () => {};

  const recorded = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    if (ignore?.(url)) return original.call(target, input, init);
    const method = (init?.method ?? (typeof input === "object" && "method" in input ? input.method : "GET")).toUpperCase();
    const start = Date.now();
    setInFlight(1);
    try {
      const res = await original.call(target, input, init);
      recordApiCall({ timestamp: start, method, path: redactUrl(url), status: res.status, durationMs: Date.now() - start, error: res.ok ? null : `${res.status}` });
      return res;
    } catch (err: unknown) {
      const name = (err as { name?: string })?.name;
      const error = name === "AbortError" || name === "TimeoutError" ? "Aborted or timed out" : redactString((err as { message?: string })?.message ?? String(err));
      recordApiCall({ timestamp: start, method, path: redactUrl(url), status: null, durationMs: Date.now() - start, error });
      throw err;
    } finally {
      setInFlight(-1);
    }
  };
  (recorded as { __recorded?: boolean }).__recorded = true;
  target.fetch = recorded as typeof fetch;
  return () => {
    if (target.fetch === recorded) target.fetch = original;
  };
}

// ---------------------------------------------------------------------------
// Optional: device details
// ---------------------------------------------------------------------------

/**
 * What can be learned about the device without a native module. On React
 * Native that is only the platform: merge in Platform.OS and the like there.
 * The page path is included, never the full URL with its query.
 */
export function deviceInfo(): Record<string, unknown> {
  const g = globalThis as {
    navigator?: { product?: string; userAgent?: string; language?: string; onLine?: boolean };
    innerWidth?: number;
    innerHeight?: number;
    devicePixelRatio?: number;
    screen?: { width?: number; height?: number };
    location?: { pathname?: string };
  };
  if (g.navigator?.product === "ReactNative") return { platform: "react-native" };

  const info: Record<string, unknown> = { platform: "web" };
  if (g.navigator) {
    info.userAgent = g.navigator.userAgent;
    info.language = g.navigator.language;
    info.online = g.navigator.onLine;
  }
  if (typeof g.innerWidth === "number") info.viewport = `${g.innerWidth}x${g.innerHeight}`;
  if (g.screen?.width) info.screen = `${g.screen.width}x${g.screen.height}@${g.devicePixelRatio ?? 1}x`;
  try {
    info.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    // Intl is missing on some older Hermes builds.
  }
  if (g.location?.pathname) info.page = g.location.pathname;
  return info;
}
